
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useGame } from '../contexts/GameContext';
import { Lightbulb, Target, Book, CheckSquare } from 'lucide-react';

const CareerGuidance: React.FC = () => {
  const { selectedRole, careerLevel, skills, experience, yearsPassed, kpis } = useGame();
  
  if (!selectedRole) return null;
  
  const currentPathInfo = selectedRole.careerPath.find(path => path.level === careerLevel);
  const nextPathInfo = selectedRole.careerPath.find(path => 
    path.requiredExperience > experience && path.requiredYears > 0
  );
  
  const getRequiredSkills = () => {
    const currentSkillIds = skills.map(s => s.id);
    return selectedRole.careerObjectives.filter(obj => 
      !currentSkillIds.includes(obj.reward.toLowerCase().replace(' ', '-'))
    );
  };

  // Calculate KPI progress relative to targets
  const calculateKpiProgress = (kpi: string, target: number, higherIsBetter: boolean) => {
    // @ts-ignore - we know kpi is a valid key
    const current = kpis[kpi];
    if (higherIsBetter) {
      return Math.min(100, (current / target) * 100);
    } else {
      // Lower is better (like energy cost)
      return Math.min(100, (target / current) * 100);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Lightbulb className="mr-2 text-bitcoin-primary" /> Career Progression Guide
        </CardTitle>
        <CardDescription>Follow these steps to advance in your career</CardDescription>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[300px] pr-4">
          <div className="space-y-6">
            <div>
              <h3 className="flex items-center text-lg font-semibold mb-2">
                <Target className="mr-2 h-5 w-5 text-bitcoin-primary" /> Current Position Goals
              </h3>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  As a {currentPathInfo?.title}, focus on:
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Maintain KPIs above industry standards</li>
                  <li>Complete challenges to gain experience ({experience} / {nextPathInfo?.requiredExperience} XP needed)</li>
                  <li>Build expertise in your core responsibilities</li>
                </ul>
              </div>
            </div>

            <div>
              <h3 className="flex items-center text-lg font-semibold mb-2">
                <Book className="mr-2 h-5 w-5 text-bitcoin-primary" /> Required Skills
              </h3>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  To advance to {nextPathInfo?.title}, develop these skills:
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  {getRequiredSkills().map((obj, index) => (
                    <li key={index}>{obj.title}: {obj.description}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div>
              <h3 className="flex items-center text-lg font-semibold mb-2">
                <CheckSquare className="mr-2 h-5 w-5 text-bitcoin-primary" /> Actions To Improve Performance
              </h3>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Take these actions to advance faster:</p>
                <ul className="list-disc list-inside space-y-2 text-sm">
                  <li>
                    <strong>Advance Time:</strong> Click the "Advance Time" button to progress years and improve KPIs naturally
                  </li>
                  <li>
                    <strong>Complete Challenges:</strong> When challenges appear, choose options that align with your career goals
                  </li>
                  <li>
                    <strong>Strategic Choices:</strong> Different challenge options affect different KPIs:
                    <ul className="ml-6 mt-1 list-disc">
                      <li>Technical options improve efficiency and uptime</li>
                      <li>Business options improve profit margin</li>
                      <li>Operations options reduce energy costs</li>
                    </ul>
                  </li>
                  <li>
                    <strong>Gain Skills:</strong> Choose challenge options that unlock new skills to boost your KPIs permanently
                  </li>
                </ul>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2">KPI Targets</h3>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span>Uptime (Target: 99%)</span>
                    <span>{kpis.uptime.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div className="bg-bitcoin-primary h-2 rounded-full" style={{ 
                      width: `${calculateKpiProgress('uptime', 99, true)}%` 
                    }}></div>
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span>Energy Cost (Target: $0.05/kWh)</span>
                    <span>${kpis.energyCost.toFixed(3)}/kWh</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div className="bg-bitcoin-primary h-2 rounded-full" style={{ 
                      width: `${calculateKpiProgress('energyCost', 0.05, false)}%` 
                    }}></div>
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span>Profit Margin (Target: 25%)</span>
                    <span>{kpis.profitMargin.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div className="bg-bitcoin-primary h-2 rounded-full" style={{ 
                      width: `${calculateKpiProgress('profitMargin', 25, true)}%` 
                    }}></div>
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span>Efficiency (Target: 85%)</span>
                    <span>{kpis.efficiency.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div className="bg-bitcoin-primary h-2 rounded-full" style={{ 
                      width: `${calculateKpiProgress('efficiency', 85, true)}%` 
                    }}></div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2">Advancement Timeline</h3>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Key milestones for promotion:</p>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  {nextPathInfo && (
                    <>
                      <li>Required Years: {nextPathInfo.requiredYears} years (Current: {yearsPassed})</li>
                      <li>Required Experience: {nextPathInfo.requiredExperience} XP (Current: {experience})</li>
                      <li>Maintain consistent KPI improvements</li>
                      <li>Successfully handle increasing challenge difficulty</li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default CareerGuidance;
